import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from "react";
import { supabase } from "../lib/supabase";

interface CreditsContextType {
  credits: number;
  ready: boolean;
  refresh: () => Promise<void>;
  add: (
    amount: number,
    promoCode?: string,
  ) => Promise<{ bonusApplied: boolean; error: string | null }>;
  error: string | null;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

const PROMO_CODES: Record<string, number> = { BONUS5: 5 };

export function CreditsProvider({ children }: { children: ReactNode }) {
  const [credits, setCredits] = useState(0);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCurrentUserId = useCallback(async () => {
    const { data, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    return data.user?.id ?? null;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        setCredits(0);
        setError(null);
        setReady(true);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("credits")
        .eq("id", userId)
        .single();

      if (profileError) {
        setCredits(0);
        setError(profileError.message);
      } else {
        setCredits(profile?.credits ?? 0);
        setError(null);
      }
    } catch (err) {
      setCredits(0);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setReady(true);
    }
  }, [getCurrentUserId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);
  const add = useCallback(
    async (amount: number, promoCode?: string) => {
      if (amount <= 0) {
        const message = "Le montant doit être positif";
        setError(message);
        return { bonusApplied: false, error: message };
      }

      try {
        const userId = await getCurrentUserId();
        if (!userId) {
          const message = "Utilisateur non connecté";
          setError(message);
          return { bonusApplied: false, error: message };
        }

        const code = promoCode?.trim().toUpperCase();
        const bonus = code ? (PROMO_CODES[code] ?? 0) : 0;

        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("credits")
          .eq("id", userId)
          .single();

        if (profileError) {
          const message = profileError.message;
          setError(message);
          return { bonusApplied: false, error: message };
        }

        const currentCredits = profile?.credits ?? 0;
        const newCredits = currentCredits + amount + bonus;

        const { error: updateError } = await supabase
          .from("profiles")
          .update({ credits: newCredits })
          .eq("id", userId);

        if (updateError) {
          const message = updateError.message;
          setError(message);
          return { bonusApplied: false, error: message };
        }

        const { error: insertError } = await supabase.from("credit_logs").insert({
          user_id: userId,
          delta: amount + bonus,
          description: bonus > 0 ? `Achat de crédits (code ${code})` : "Achat de crédits",
        });

        if (insertError) {
          const message = insertError.message;
          setCredits(newCredits);
          setError(message);
          setReady(true);
          return { bonusApplied: bonus > 0, error: message };
        }

        setCredits(newCredits);
        setError(null);
        setReady(true);
        return { bonusApplied: bonus > 0, error: null };
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        return { bonusApplied: false, error: message };
      }
    },
    [getCurrentUserId],
  );

  return (
    <CreditsContext.Provider value={{ credits, ready, refresh, add, error }}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const ctx = useContext(CreditsContext);
  if (!ctx) throw new Error("useCredits must be used within CreditsProvider");
  return ctx;
}
